import { useState } from 'react';
import type { Client, WorkoutAssignment, WorkoutSession } from '../../types';
import { SectionCard } from './SectionCard';
import { ClientForm } from './ClientForm';
import { ClientList } from './ClientList';
import { Panel } from '../ui/Panel';
import { Button } from '../ui/Button';
import { ALERTS, BORDERS } from '../../styles/classNames';
import { formatPhoneE164, isValidPhone } from '@/shared/utils/phone';

interface ClientsPanelProps {
  clients: Client[];
  assignments: WorkoutAssignment[];
  sessions?: WorkoutSession[];
  onAddClient: (data: { displayName: string; phone: string }) => Promise<unknown>;
  error?: string | null;
  onSelectClient?: (id: string) => void;
}

export function ClientsPanel({
  clients,
  assignments,
  sessions = [],
  onAddClient,
  onSelectClient,
  error,
}: ClientsPanelProps) {
  const [clientDraft, setClientDraft] = useState({ displayName: '', phone: '' });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  // Count assignments per client
  const assignmentCounts = assignments.reduce((acc, a) => {
    acc[a.clientId] = (acc[a.clientId] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Count sessions per client
  const sessionCounts = sessions.reduce((acc, s) => {
    acc[s.clientId] = (acc[s.clientId] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const handleAddClient = async () => {
    const normalizedPhone = formatPhoneE164(clientDraft.phone.trim());

    if (!isValidPhone(clientDraft.phone) || !normalizedPhone.startsWith('+')) {
      setLocalError('Enter a valid phone number. Example: +16025551234');
      return;
    }

    await onAddClient({ ...clientDraft, phone: normalizedPhone });
    setClientDraft({ displayName: '', phone: '' });
    setLocalError(null);
    setIsAddModalOpen(false);
  };

  return (
    <>
      <SectionCard
        eyebrow="Client Management"
        title="Your Clients"
        fillHeight
        action={
          <Button onClick={() => setIsAddModalOpen(true)} size="sm">
            + Add Client
          </Button>
        }
      >
        {(error || localError) && (
          <div className={`mb-4 ${ALERTS.error} shrink-0`}>
            {error || localError}
          </div>
        )}
        <ClientList
          clients={clients}
          assignmentCounts={assignmentCounts}
          sessionCounts={sessionCounts}
          onSelect={onSelectClient}
          search={clientSearch}
          onSearchChange={setClientSearch}
        />
      </SectionCard>

      {/* Add Client Panel */}
      <Panel
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Client"
        variant="modal"
        size="sm"
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Add a client by phone number. They will not be linked to your account until they accept the invite after signing in.
          </p>

          <ClientForm
            displayName={clientDraft.displayName}
            phone={clientDraft.phone}
            onChange={setClientDraft}
            hideSubmitButton
          />

          <div className="p-3 rounded-lg border border-primary-500/30 bg-primary-500/10 text-sm text-gray-700 dark:text-slate-300">
            Use full international format when possible. US numbers entered locally will be normalized to E.164.
          </div>

          <div className={`pt-4 ${BORDERS.top} space-y-2`}>
            <Button className="w-full" onClick={handleAddClient} disabled={!clientDraft.displayName.trim() || !clientDraft.phone.trim()}>
              Add Client
            </Button>
            <Button variant="secondary" className="w-full" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Panel>
    </>
  );
}
